import { apiService } from './api.js';

/**
 * Webhooks Service
 * Handles all webhook related operations
 */
class WebhooksService {
  /**
   * Create a new webhook
   * @param {string} projectId - Project ID
   * @param {string} name - Webhook name
   * @param {string} url - Webhook URL
   * @param {Array<string>} events - Array of webhook events
   * @param {boolean} isActive - Whether webhook is active (default: true)
   * @returns {Promise<Object>} Created webhook data
   */
  async createWebhook(projectId, name, url, events, isActive = true) {
    try {
      const response = await apiService.post('/webhooks', {
        projectId,
        name,
        url,
        events,
        isActive,
      });
      return response;
    } catch (error) {
      console.error('Error creating webhook:', error);
      throw error;
    }
  }

  /**
   * Get webhooks with pagination
   * @param {string} projectId - Project ID
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @param {boolean} isActive - Filter by active status (optional)
   * @returns {Promise<Object>} Webhooks data with pagination
   */
  async getWebhooks(projectId, page = 1, limit = 10, isActive = null) {
    try {
      const params = new URLSearchParams({
        projectId,
        page: page.toString(),
        limit: limit.toString(),
      });

      if (isActive !== null) {
        params.append('isActive', isActive.toString());
      }

      const response = await apiService.get(`/webhooks?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      throw error;
    }
  }

  /**
   * Get webhook by ID
   * @param {string} webhookId - Webhook ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Webhook data
   */
  async getWebhookById(webhookId, projectId) {
    try {
      const params = new URLSearchParams({ projectId });
      const response = await apiService.get(`/webhooks/${webhookId}?${params}`);
      return response;
    } catch (error) {
      console.error('Error fetching webhook:', error);
      throw error;
    }
  }

  /**
   * Update webhook
   * @param {string} webhookId - Webhook ID
   * @param {string} projectId - Project ID
   * @param {string} name - New webhook name
   * @param {string} url - New webhook URL
   * @param {Array<string>} events - New array of webhook events
   * @param {boolean} isActive - New active status
   * @returns {Promise<Object>} Updated webhook data
   */
  async updateWebhook(webhookId, projectId, name, url, events, isActive) {
    try {
      const response = await apiService.put(`/webhooks/${webhookId}`, {
        projectId,
        webhookId,
        name,
        url,
        events,
        isActive,
      });
      return response;
    } catch (error) {
      console.error('Error updating webhook:', error);
      throw error;
    }
  }

  /**
   * Delete webhook
   * @param {string} webhookId - Webhook ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteWebhook(webhookId, projectId) {
    try {
      const params = new URLSearchParams({
        projectId,
        webhookId,
      });
      const response = await apiService.delete(
        `/webhooks/${webhookId}?${params}`
      );
      return response;
    } catch (error) {
      console.error('Error deleting webhook:', error);
      throw error;
    }
  }

  /**
   * Test webhook
   * @param {string} webhookId - Webhook ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Test result
   */
  async testWebhook(webhookId, projectId) {
    try {
      const response = await apiService.post(`/webhooks/${webhookId}/test`, {
        projectId,
        webhookId,
      });
      return response;
    } catch (error) {
      console.error('Error testing webhook:', error);
      throw error;
    }
  }

  /**
   * Get webhook analytics
   * @param {string} webhookId - Webhook ID
   * @param {string} projectId - Project ID
   * @param {string} startDate - Start date for analytics (optional)
   * @param {string} endDate - End date for analytics (optional)
   * @returns {Promise<Object>} Analytics data
   */
  async getWebhookAnalytics(
    webhookId,
    projectId,
    startDate = null,
    endDate = null
  ) {
    try {
      const params = new URLSearchParams({
        projectId,
        webhookId,
      });

      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await apiService.get(
        `/webhooks/${webhookId}/analytics?${params}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching webhook analytics:', error);
      throw error;
    }
  }

  /**
   * Get available webhook events
   * @returns {Array<Object>} Array of available webhook events
   */
  getAvailableEvents() {
    return [
      { id: 'contact.created', label: 'Contact Created' },
      { id: 'contact.updated', label: 'Contact Updated' },
      { id: 'contact.deleted', label: 'Contact Deleted' },
      { id: 'message.received', label: 'Message Received' },
      { id: 'message.sent', label: 'Message Sent' },
      { id: 'conversation.created', label: 'Conversation Created' },
      { id: 'conversation.updated', label: 'Conversation Updated' },
      { id: 'project.created', label: 'Project Created' },
      { id: 'project.updated', label: 'Project Updated' },
    ];
  }
}

// Export singleton instance
export const webhooksService = new WebhooksService();
